import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { Message } from 'primeng/message';
import { AuthService } from '@/core/auth/auth.service';
import { KeycloakLoginService } from '@/core/auth/keycloak-login.service';

/**
 * Tela /login: não tem formulário. Se o usuário não está logado e não há erro,
 * redireciona automaticamente para o Keycloak (SSO). Quando o callback reprova
 * (sem acesso) ou falha, exibe a mensagem e um botão para tentar de novo.
 */
@Component({
    selector: 'app-login',
    standalone: true,
    imports: [ButtonModule, Message],
    templateUrl: './login.html',
    styleUrl: './login.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent implements OnInit {
    private readonly authService = inject(AuthService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly login = inject(KeycloakLoginService);

    readonly erro = signal<string | null>(null);

    async ngOnInit(): Promise<void> {
        if (this.authService.isLoggedIn) {
            // Já autenticado: devolve ao destino original em vez de descartá-lo na raiz.
            await this.router.navigateByUrl(this.returnUrl());
            return;
        }
        const erro = this.route.snapshot.queryParamMap.get('erro');
        if (erro === 'sem-acesso') {
            this.erro.set('Seu usuário não tem permissão de acesso ao SICOM.');
            return;
        }
        if (erro === 'certificado' || erro === 'falha') {
            this.erro.set('Não foi possível concluir o login. Tente novamente.');
            return;
        }
        await this.entrar();
    }

    /** Destino pretendido pelo usuário, preservado pelo AuthGuard ao barrar a rota. */
    private returnUrl(): string {
        return this.route.snapshot.queryParamMap.get('returnUrl') ?? '/';
    }

    /** Dispara o login pelo Keycloak (redirect). */
    async entrar(): Promise<void> {
        this.erro.set(null);
        try {
            await this.login.iniciar(this.returnUrl());
        } catch {
            this.erro.set('Não foi possível iniciar o login. Tente novamente.');
        }
    }

    /**
     * "Entrar novamente" após uma falha: encerra a sessão (SICOM + SSO do Keycloak),
     * limpando cookies e estado local, e reinicia o login do zero — permitindo que o
     * usuário re-selecione as credenciais em vez de reaproveitar a sessão SSO que falhou.
     */
    async entrarNovamente(): Promise<void> {
        this.erro.set(null);
        try {
            // Revoga o refresh no backend e limpa o estado local (best-effort).
            await this.authService.logout({ skipNavigate: true });
        } catch {
            // best-effort: segue para o logout do Keycloak mesmo assim.
        }
        // Encerra a sessão SSO no Keycloak (limpa o cookie no domínio dele) e volta ao app,
        // de onde um novo login é iniciado do zero.
        this.login.logoutRedirect();
    }
}
